import { generatePath } from 'react-router-dom';
import { AlertCategory } from '~/common/models/alert.model';
import {
  SETTINGS_NOTIFICATIONS_ACCOUNT_DETAIL,
  SETTINGS_NOTIFICATIONS_SAFETY_AND_SECURITY_DETAIL,
  SETTINGS_NOTIFICATIONS_VEHICLES_DETAIL,
  SETTINGS_NOTIFICATIONS_VEHICLES_DIAGNOSTICS_DETAIL,
  SETTINGS_NOTIFICATIONS_VEHICLES_GEOFENCE_DETAIL,
} from '~/common/models/pages/settingsPages.model';
import { DEFAULT_DIAGNOSTICS_KEY } from '~/routes/settings/settings.constant';

type NotificationDetailPathParams = {
  /** Alert category of the notification. */
  category?: string;
  /** Alert event of the notification. */
  event?: string;
  /** Subscription of a diagnostics notification; the default one may have none yet. */
  subscriptionId?: string;
  /** Map rule of a geofence notification. */
  ruleId?: string;
};

const PATH_BY_EVENT_CATEGORY: Partial<Record<string, string>> = {
  [AlertCategory.PROFILE]: SETTINGS_NOTIFICATIONS_ACCOUNT_DETAIL,
  [AlertCategory.SAFETY]: SETTINGS_NOTIFICATIONS_SAFETY_AND_SECURITY_DETAIL,
  [AlertCategory.ASSET]: SETTINGS_NOTIFICATIONS_VEHICLES_DETAIL,
};

/**
 * Path of the full-page settings for a notification. Account, safety and
 * vehicle activity notifications open by event, diagnostics by subscription
 * and geofence notifications by map rule.
 * @param params.category - Alert category of the notification
 * @param params.event - Alert event of the notification
 * @param params.subscriptionId - Subscription id, used for diagnostics
 * @param params.ruleId - Map rule id, used for geofences
 * @returns The detail page path, or undefined when the notification has no detail page
 */
export const buildNotificationDetailPath = ({
  category,
  event,
  subscriptionId,
  ruleId,
}: NotificationDetailPathParams): string | undefined => {
  if (category === AlertCategory.DIAGNOSTIC) {
    return generatePath(SETTINGS_NOTIFICATIONS_VEHICLES_DIAGNOSTICS_DETAIL, {
      subscriptionId: subscriptionId || DEFAULT_DIAGNOSTICS_KEY,
    });
  }
  if (category === AlertCategory.GEOFENCE) {
    return ruleId
      ? generatePath(SETTINGS_NOTIFICATIONS_VEHICLES_GEOFENCE_DETAIL, {
          ruleId,
        })
      : undefined;
  }
  const path = category ? PATH_BY_EVENT_CATEGORY[category] : undefined;
  if (!path || !event) return undefined;
  return generatePath(path, { notificationKey: event });
};
